import React from "react";
import {
  MessageSquareHeart,
  Search,
  Filter,
  RefreshCw,
  Star,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  AlertCircle,
  Activity,
  BarChart3,
} from "lucide-react";
import {
  listReviewsAdmin,
  setReviewStatus,
  getEngagementAnalytics,
  getReviewSummary,
  parseCallbackReview,
} from "../../api/reviews.js";

function StatusPill({ status }) {
  const s = String(status || "Pending").toLowerCase();
  const base = "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium";
  if (s.includes("approv") || s.includes("publish")) {
    return (
      <span className={`${base} bg-emerald-100 text-emerald-700`}>
        <CheckCircle2 className="h-3.5 w-3.5" />
        {status || "Published"}
      </span>
    );
  }
  if (s.includes("reject") || s.includes("hidden") || s.includes("spam")) {
    return (
      <span className={`${base} bg-rose-100 text-rose-700`}>
        <XCircle className="h-3.5 w-3.5" />
        {status || "Rejected"}
      </span>
    );
  }
  return (
    <span className={`${base} bg-amber-100 text-amber-800`}>
      <Clock className="h-3.5 w-3.5" />
      {status || "Pending"}
    </span>
  );
}

function Stars({ rating }) {
  const n = Number(rating) || 0;
  if (!n) return <span className="text-xs text-slate-400">—</span>;
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i <= n ? "text-amber-400 fill-amber-400" : "text-slate-200"}`}
        />
      ))}
    </span>
  );
}

function formatSeconds(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  if (n < 60) return `${n.toFixed(n % 1 ? 1 : 0)}s`;
  const mins = Math.floor(n / 60);
  const secs = Math.round(n % 60);
  return `${mins}m ${secs}s`;
}

function friendlyPageLabel(name) {
  const raw = String(name || "");
  if (raw.includes("buy-to-let-mortgage-calculator")) return "BTL sourcing";
  if (raw.includes("invoice-finance-sourcing")) return "IF sourcing";
  return raw;
}

function mapToRows(mapObj) {
  if (!mapObj || typeof mapObj !== "object") return [];
  return Object.entries(mapObj).map(([name, stats]) => ({
    name,
    views: Number(stats?.views) || 0,
    total_seconds: Number(stats?.total_seconds) || 0,
    avg_seconds: Number(stats?.avg_seconds) || 0,
    avg_scroll_percent:
      stats?.avg_scroll_percent != null ? Number(stats.avg_scroll_percent) : null,
  }));
}

function EngagementTable({ title, rows, nameLabel = "Name" }) {
  if (!rows.length) {
    return (
      <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-4 text-xs text-slate-500">
        No {title.toLowerCase()} data yet.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="min-w-full text-xs">
        <thead className="bg-slate-50 text-slate-500">
          <tr>
            <th className="px-3 py-2 text-left font-medium">{nameLabel}</th>
            <th className="px-3 py-2 text-right font-medium">Views</th>
            <th className="px-3 py-2 text-right font-medium">Total time</th>
            <th className="px-3 py-2 text-right font-medium">Avg time</th>
            <th className="px-3 py-2 text-right font-medium">Avg scroll</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.name} className="border-t border-slate-100">
              <td className="px-3 py-2 text-slate-800 font-medium">
                <div>{friendlyPageLabel(row.name)}</div>
                {friendlyPageLabel(row.name) !== row.name && (
                  <div className="text-[10px] text-slate-400 font-mono break-all">{row.name}</div>
                )}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-700">{row.views}</td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                {formatSeconds(row.total_seconds)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                {formatSeconds(row.avg_seconds)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                {row.avg_scroll_percent != null ? `${row.avg_scroll_percent}%` : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ReviewsEngagementPage() {
  const [reviews, setReviews] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [engagement, setEngagement] = React.useState(null);
  const [engagementNote, setEngagementNote] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [busyId, setBusyId] = React.useState(null);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("All");
  const [categoryFilter, setCategoryFilter] = React.useState("All");
  const [usedFallback, setUsedFallback] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [listRes, summaryRes, engRes] = await Promise.all([
        listReviewsAdmin({ page: 1, page_size: 100, search: search.trim() || undefined }),
        getReviewSummary({}).catch(() => null),
        getEngagementAnalytics({}).catch((e) => ({ ok: false, error: e?.message })),
      ]);

      setReviews(Array.isArray(listRes.data) ? listRes.data : []);
      setUsedFallback(Boolean(listRes.fallback));
      setSummary(listRes.summary || summaryRes || null);

      if (engRes?.skipped || engRes?.reason === "not_deployed") {
        setEngagement(null);
        setEngagementNote(
          "Engagement analytics API is not available (dynesourcerapi.engagement_tracking.get_engagement_analytics)."
        );
      } else if (engRes?.ok && engRes?.data) {
        setEngagement(engRes.data);
        setEngagementNote("");
      } else {
        setEngagement(null);
        setEngagementNote(engRes?.error || engRes?.message || "Failed to load engagement analytics.");
      }
    } catch (err) {
      setError(err?.message || "Failed to load reviews");
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  React.useEffect(() => {
    load();
  }, [load]);

  const filtered = React.useMemo(() => {
    return reviews.filter((r) => {
      if (parseCallbackReview(r).isCallback) return false;
      const status = String(r.status || r.review_status || "Pending");
      const cat = String(r.product_category || "");
      if (statusFilter !== "All" && !status.toLowerCase().includes(statusFilter.toLowerCase())) {
        return false;
      }
      if (categoryFilter !== "All" && cat.toUpperCase() !== categoryFilter.toUpperCase()) {
        return false;
      }
      return true;
    });
  }, [reviews, statusFilter, categoryFilter]);

  const updateStatus = async (review, status) => {
    const id = review.name || review.id;
    if (!id) return;
    setBusyId(id);
    setNotice("");
    try {
      await setReviewStatus({ review: id, status });
      setReviews((prev) =>
        prev.map((r) =>
          (r.name || r.id) === id ? { ...r, status, review_status: status } : r
        )
      );
      setNotice(`Marked as ${status}`);
      setTimeout(() => setNotice(""), 2500);
    } catch (err) {
      setError(err?.message || "Failed to update status");
    } finally {
      setBusyId(null);
    }
  };

  const avg = summary?.average_rating ?? summary?.avg_rating;
  const total = summary?.total_reviews ?? filtered.length;
  const eng = engagement && typeof engagement === "object" ? engagement : null;
  const pageRows = React.useMemo(() => {
    if (!eng) return [];
    if (Array.isArray(eng.most_engaged_pages) && eng.most_engaged_pages.length) {
      return eng.most_engaged_pages.map((row) => ({
        name: row.name,
        views: Number(row.views) || 0,
        total_seconds: Number(row.total_seconds) || 0,
        avg_seconds: Number(row.avg_seconds) || 0,
        avg_scroll_percent:
          row.avg_scroll_percent != null ? Number(row.avg_scroll_percent) : null,
      }));
    }
    return mapToRows(eng.by_page);
  }, [eng]);
  const sectionRows = React.useMemo(() => {
    if (!eng) return [];
    if (Array.isArray(eng.most_engaged_sections) && eng.most_engaged_sections.length) {
      return eng.most_engaged_sections.map((row) => ({
        name: row.name,
        views: Number(row.views) || 0,
        total_seconds: Number(row.total_seconds) || 0,
        avg_seconds: Number(row.avg_seconds) || 0,
        avg_scroll_percent:
          row.avg_scroll_percent != null ? Number(row.avg_scroll_percent) : null,
      }));
    }
    return mapToRows(eng.by_section);
  }, [eng]);
  const userTypeRows = React.useMemo(() => mapToRows(eng?.by_user_type), [eng]);

  return (
    <div className="space-y-4">
      {notice && (
        <div className="fixed top-4 right-4 z-50 px-4 py-3 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs font-medium shadow-lg flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" />
          {notice}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-2xl bg-sky-50 flex items-center justify-center">
            <MessageSquareHeart className="h-5 w-5 text-sky-600" />
          </div>
          <div>
            <h1 className="text-sm md:text-base font-semibold text-slate-900">
              Reviews & Engagement
            </h1>
            <p className="text-[11px] text-slate-500">
              Moderate feedback and monitor sourcing engagement
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] text-slate-500 font-medium">Total reviews</div>
          <div className="mt-1 text-2xl font-bold text-slate-900">{total ?? "—"}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] text-slate-500 font-medium">Average rating</div>
          <div className="mt-1 text-2xl font-bold text-slate-900">
            {avg != null ? Number(avg).toFixed(1) : "—"}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] text-slate-500 font-medium">Rated reviews</div>
          <div className="mt-1 text-2xl font-bold text-slate-900">
            {summary?.rated_reviews ?? "—"}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[11px] text-slate-500 font-medium flex items-center gap-1">
            <Activity className="h-3.5 w-3.5" /> Engagement events
          </div>
          <div className="mt-1 text-2xl font-bold text-slate-900">
            {eng?.total_events != null ? eng.total_events : "—"}
          </div>
          {eng?.total_sessions != null && (
            <div className="mt-0.5 text-[11px] text-slate-500">
              {eng.total_sessions} session{eng.total_sessions === 1 ? "" : "s"}
            </div>
          )}
        </div>
      </div>

      {(engagementNote || usedFallback) && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 flex gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            {usedFallback && (
              <p>
                Showing public review list (admin list endpoint unavailable). Status actions may
                fail until <code>list_reviews_admin</code> / <code>set_review_status</code> are
                permitted for your admin user.
              </p>
            )}
            {engagementNote && <p className={usedFallback ? "mt-1" : ""}>{engagementNote}</p>}
          </div>
        </div>
      )}

      {eng && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-sky-600" />
            <h2 className="text-sm font-semibold text-slate-900">Engagement analytics</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
              <div className="text-[11px] text-slate-500">Total events</div>
              <div className="text-lg font-bold text-slate-900">{eng.total_events ?? "—"}</div>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
              <div className="text-[11px] text-slate-500">Total time</div>
              <div className="text-lg font-bold text-slate-900">
                {formatSeconds(eng.total_time_seconds)}
              </div>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
              <div className="text-[11px] text-slate-500">Sessions</div>
              <div className="text-lg font-bold text-slate-900">{eng.total_sessions ?? "—"}</div>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
              <div className="text-[11px] text-slate-500">Avg / event</div>
              <div className="text-lg font-bold text-slate-900">
                {formatSeconds(eng.avg_seconds_per_event)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xs font-semibold text-slate-700 mb-2">Most engaged pages</h3>
              <EngagementTable title="Pages" rows={pageRows} nameLabel="Page" />
            </div>
            <div>
              <h3 className="text-xs font-semibold text-slate-700 mb-2">Most engaged sections</h3>
              <EngagementTable title="Sections" rows={sectionRows} nameLabel="Section" />
            </div>
          </div>

          {userTypeRows.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-700 mb-2">By user type</h3>
              <EngagementTable title="User types" rows={userTypeRows} nameLabel="User type" />
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 px-4 py-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 flex-1 min-w-[180px]">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search reviews..."
            className="bg-transparent text-xs outline-none w-full"
          />
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 text-xs">
          <Filter className="h-4 w-4 text-slate-500" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-transparent outline-none"
          >
            <option>All</option>
            <option>Pending</option>
            <option>Published</option>
            <option>Rejected</option>
            <option>Spam</option>
          </select>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 text-xs">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="bg-transparent outline-none"
          >
            <option>All</option>
            <option>BTL</option>
            <option>IF</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-800">
          {error}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Submitted</th>
              <th className="px-4 py-3 text-left font-medium">Reviewer</th>
              <th className="px-4 py-3 text-left font-medium">Type</th>
              <th className="px-4 py-3 text-left font-medium">Category</th>
              <th className="px-4 py-3 text-left font-medium">Rating</th>
              <th className="px-4 py-3 text-left font-medium">Feedback</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                  <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                  Loading reviews...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                  No reviews found.
                </td>
              </tr>
            ) : (
              filtered.map((r) => {
                const id = r.name || r.id;
                const busy = busyId === id;
                return (
                  <tr key={id} className="border-b border-slate-100 last:border-0 align-top">
                    <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">
                      {r.submitted_on ? String(r.submitted_on).slice(0, 16) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-900 font-medium text-xs">
                        {r.reviewer_name || "Anonymous"}
                      </div>
                      <div className="text-[11px] text-slate-500">{r.user_type || "Guest"}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">{r.review_type || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-700">
                      {r.product_category || "—"}
                      {r.product ? (
                        <div className="text-[11px] text-slate-400 font-mono">{r.product}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <Stars rating={r.rating} />
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      {r.title && (
                        <div className="text-xs font-semibold text-slate-900 mb-0.5">{r.title}</div>
                      )}
                      <div className="text-xs text-slate-600 line-clamp-3">{r.review_text}</div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={r.status || r.review_status || "Pending"} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1.5">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => updateStatus(r, "Published")}
                          className="text-[11px] font-semibold text-emerald-700 hover:underline disabled:opacity-50"
                        >
                          Publish
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => updateStatus(r, "Rejected")}
                          className="text-[11px] font-semibold text-rose-700 hover:underline disabled:opacity-50"
                        >
                          Reject
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => updateStatus(r, "Spam")}
                          className="text-[11px] font-semibold text-rose-600 hover:underline disabled:opacity-50"
                        >
                          Spam
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => updateStatus(r, "Pending")}
                          className="text-[11px] font-semibold text-slate-600 hover:underline disabled:opacity-50"
                        >
                          Pending
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
